<template>
  <div class="transaction">
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells><h1 class="title">Balance</h1></app-cells>
      <app-cells>
        <app-button @click.native="onShowModal(null)" theme="outcome">
          <img src="@/assets/img/icon-outcome.svg" alt="Outcome">
          Outcome
        </app-button>
        <app-button @click.native="onShowModalExchange" theme="info">
          <img src="@/assets/img/icon-transfer-transaction.svg" alt="Office">
          Exchange
        </app-button>
        <app-button @click.native="onShowModalBalance">Post balance</app-button>
        <app-button @click.native="onShowModalTransfer" theme="info">
          <img src="@/assets/img/icon-transfer-transaction.svg" alt="Office">
          Money to office
        </app-button>
      </app-cells>
      <div class="transaction__table">
        <div class="transaction__table-row transaction__table-row--head">
          <div class="transaction__table-col">Date</div>
          <div class="transaction__table-col">Debit</div>
          <div class="transaction__table-col">Credit</div>
          <div class="transaction__table-col">Item / Description</div>
          <div class="transaction__table-col">Account</div>
          <div class="transaction__table-col"></div>
        </div>
        <div v-for="transaction of table_data" :key="transaction.id" class="transaction__table-row">
          <div class="transaction__table-col"><b>{{ dateFormat(transaction.date) }}</b></div>
          <div class="transaction__table-col">
            <template v-if="transaction.is_transfer">
              <b v-if="transaction.transfer_transaction_to" class="transaction__table-amount transaction__table-amount--green">
                {{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.transfer_transaction_to.amount) }} {{ transaction.transfer_transaction_to.account.currency }}
              </b>
            </template>
            <template v-else>
              <b v-if="transaction.type === 1" class="transaction__table-amount transaction__table-amount--green">
                {{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.amount) }} {{ transaction.account.currency }}
              </b>
              <span v-else>-</span>
            </template>
          </div>
          <div class="transaction__table-col">
            <template v-if="transaction.is_transfer">
              <b class="transaction__table-amount transaction__table-amount--red">
                -{{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.amount) }} {{ transaction.account.currency }}
              </b>
            </template>
            <template v-else>
              <b v-if="transaction.type === 2" class="transaction__table-amount transaction__table-amount--red">
                -{{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.amount) }} {{ transaction.account.currency }}
              </b>
              <span v-else>-</span>
            </template>
          </div>
          <div class="transaction__table-col transaction__table-col--flex-col">
            <b v-if="transaction.is_transfer">Transfer funds</b>
            <template v-else>
              <button
                v-if="transaction.items.length > 1"
                v-tooltip="{
                  content: tooltipContentCashflowItems(transaction),
                  placement: 'right',
                  offset: 10,
                  html: true,
                  trigger: 'click',
                  classes: 'tooltip-cashflow'
                }"
                class="transaction__table-btn"
              >
                {{ transaction.items.length }} items
                <span v-if="transaction.responsible">{{ transaction.responsible.full_name }}</span>
              </button>
              <template v-else>
                <b v-if="transaction.type === 1">{{ transaction.items[0]?.item.name_revenue }} <span v-if="transaction.responsible" class="transaction__table-description">{{ transaction.responsible.full_name }}</span></b>
                <b v-if="transaction.type === 2">{{ transaction.items[0]?.item.name_expense }} <span v-if="transaction.responsible" class="transaction__table-description">{{ transaction.responsible.full_name }}</span></b>
              </template>
            </template>
            <span v-if="transaction.description" class="transaction__table-description">{{ transaction.description }}</span>
          </div>
          <div class="transaction__table-col transaction__table-col--flex-col">
            <template v-if="transaction.is_transfer">
              <b v-if="transaction.transfer_transaction_to">{{ transaction.transfer_transaction_to.account.name }}</b>
              <b>{{ transaction.account.name }}</b>
            </template>
            <b v-else>{{ transaction.account.name }}</b>
          </div>
          <div v-if="!transaction.is_transfer" class="transaction__table-col transaction__table-col--flex">
            <button @click="onShowModal(transaction)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
            <button @click="onShowDeleteModal(transaction)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </div>
      </div>
    </template>
    <modal
      name="transaction-modal"
      :width="450"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('transaction-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">
          <template v-if="!transaction_to_action">Add transaction</template>
          <template v-else>Edit transaction</template>
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm" class="arrange-popup">
            <app-form-group label="Account" required>
              <app-select
                v-model="form.account"
                :options="account_options"
                :clearable="false"
                placeholder="Account"
                :error="$v.form.account.$error"
              />
              <template #error>
                <div v-if="$v.form.account.$dirty && !$v.form.account.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Responsible">
              <app-select
                v-model="form.responsible"
                :options="employee_options"
                :clearable="false"
                label="full_name"
                placeholder="Responsible"
              />
            </app-form-group>
            <div class="arrange-popup__part">
              <div class="arrange-popup__block-title arrange-popup__block-title--fz13"><span>Items</span></div>
              <div class="transfer-sortable transfer-sortable--balance transfer-sortable--full">
                <div v-for="(element, index) in form.items" :key="`own_item_${index}`" class="transfer-sortable__item transfer-sortable__item--mb-20">
                  <div class="transfer-sortable__row transfer-sortable__row--pr">
                    <app-form-group label="Amount" :label-for="`amount-${index}`" required class="form-group--no-indent">
                      <app-fin-number
                        v-model="element.amount"
                        :error="$v.form.items.$each[index].amount.$error"
                        :id="`amount-${index}`"
                        placeholder="0"
                        name="amount"
                      />
                      <template #error>
                        <div v-if="$v.form.items.$each[index].amount.$dirty && !$v.form.items.$each[index].amount.required">Required field</div>
                      </template>
                    </app-form-group>
                    <app-form-group label="Item" class="form-group--no-indent" required>
                      <app-select
                        v-model="element.item"
                        :options="transaction_items_options"
                        :clearable="false"
                        label="name_expense"
                        placeholder="Transaction item"
                        :error="$v.form.items.$each[index].item.$error"
                      />
                      <template #error>
                        <div v-if="$v.form.items.$each[index].item.$dirty && !$v.form.items.$each[index].item.required">Required field</div>
                      </template>
                    </app-form-group>
                    <button
                      v-if="index"
                      @click="onRemoveItem(index)"
                      class="transfer-sortable__close"
                      type="button"
                    >
                      <img src="@/assets/img/delete-transfer.svg" alt="">
                    </button>
                    <button v-if="!index" @click="onAddItem" type="button" class="transfer-sortable__add"></button>
                  </div>
                </div>
              </div>
            </div>
            <app-form-group label="Notes" label-for="description" required>
              <app-textarea
                v-model="form.description"
                rows="5"
                placeholder="Write a note"
                id="description"
                name="description"
                :error="$v.form.description.$error"
              />
              <template #error>
                <div v-if="$v.form.description.$dirty && !$v.form.description.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit" :disabled="$v.form.$error">
                {{ !transaction_to_action ? 'Add' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="transfer-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('transfer-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Transfer to office</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormTransfer">
            <app-form-group label="From account" required>
              <app-select
                v-model="form_transfer.from_account"
                :options="account_options"
                :reduce="item => item.id"
                :clearable="false"
                placeholder="Account"
                :error="$v.form_transfer.from_account.$error"
              />
              <template #error>
                <div v-if="$v.form_transfer.from_account.$dirty && !$v.form_transfer.from_account.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Amount" label-for="from-amount" required>
              <app-fin-number
                v-model="form_transfer.from_amount"
                :error="$v.form_transfer.from_amount.$error"
                id="from-amount"
                placeholder="0"
                name="from-amount"
              />
              <template #error>
                <div v-if="$v.form_transfer.from_amount.$dirty && !$v.form_transfer.from_amount.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_transfer" :disabled="$v.form_transfer.$error">Transfer</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="balance-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('balance-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Post balance</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormBalance">
            <app-grid>
              <template #item-1>
                <app-form-group label="Amount (USD)" label-for="amountUSD" required>
                  <app-fin-number
                    v-model="form_balance.usd_amount"
                    :error="$v.form_balance.usd_amount.$error"
                    id="amountUSD"
                    name="amountUSD"
                  />
                  <template #error>
                    <div v-if="$v.form_balance.usd_amount.$dirty && !$v.form_balance.usd_amount.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Amount (TZS)" label-for="amountTZS" required>
                  <app-fin-number
                    v-model="form_balance.tzs_amount"
                    :error="$v.form_balance.tzs_amount.$error"
                    id="amountTZS"
                    name="amountTZS"
                  />
                  <template #error>
                    <div v-if="$v.form_balance.tzs_amount.$dirty && !$v.form_balance.tzs_amount.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Amount (EUR)" label-for="amountEUR" required>
                  <app-fin-number
                    v-model="form_balance.eur_amount"
                    :error="$v.form_balance.eur_amount.$error"
                    id="amountEUR"
                    name="amountEUR"
                  />
                  <template #error>
                    <div v-if="$v.form_balance.eur_amount.$dirty && !$v.form_balance.eur_amount.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-grid col="3" items="3">
                  <template #item-1>
                    <app-form-group label="100$ Bills" label-for="100_bills" required>
                      <app-input
                        v-model.number="form_balance.usd_denomination.hundred"
                        :error="$v.form_balance.usd_denomination.hundred.$error"
                        id="100_bills"
                        name="100_bills"
                        type="number"
                      />
                      <template #error>
                        <div v-if="$v.form_balance.usd_denomination.hundred.$dirty && !$v.form_balance.usd_denomination.hundred.required">Required field</div>
                      </template>
                    </app-form-group>
                  </template>
                  <template #item-2>
                    <app-form-group label="50$ Bills" label-for="50_bills" required>
                      <app-input
                        v-model.number="form_balance.usd_denomination.fifty"
                        :error="$v.form_balance.usd_denomination.fifty.$error"
                        id="50_bills"
                        name="50_bills"
                        type="number"
                      />
                      <template #error>
                        <div v-if="$v.form_balance.usd_denomination.fifty.$dirty && !$v.form_balance.usd_denomination.fifty.required">Required field</div>
                      </template>
                    </app-form-group>
                  </template>
                  <template #item-3>
                    <app-form-group label="20$ Bills" label-for="20_bills" required>
                      <app-input
                        v-model.number="form_balance.usd_denomination.twenty"
                        :error="$v.form_balance.usd_denomination.twenty.$error"
                        id="20_bills"
                        name="20_bills"
                        type="number"
                      />
                      <template #error>
                        <div v-if="$v.form_balance.usd_denomination.twenty.$dirty && !$v.form_balance.usd_denomination.twenty.required">Required field</div>
                      </template>
                    </app-form-group>
                  </template>
                </app-grid>
                <app-grid col="3" items="3">
                  <template #item-1>
                    <app-form-group label="10$ Bills" label-for="10_bills" required>
                      <app-input
                        v-model.number="form_balance.usd_denomination.ten"
                        :error="$v.form_balance.usd_denomination.ten.$error"
                        id="10_bills"
                        name="10_bills"
                        type="number"
                      />
                      <template #error>
                        <div v-if="$v.form_balance.usd_denomination.ten.$dirty && !$v.form_balance.usd_denomination.ten.required">Required field</div>
                      </template>
                    </app-form-group>
                  </template>
                  <template #item-2>
                    <app-form-group label="5$ Bills" label-for="5_bills" required>
                      <app-input
                        v-model.number="form_balance.usd_denomination.five"
                        :error="$v.form_balance.usd_denomination.five.$error"
                        id="5_bills"
                        name="5_bills"
                        type="number"
                      />
                      <template #error>
                        <div v-if="$v.form_balance.usd_denomination.five.$dirty && !$v.form_balance.usd_denomination.five.required">Required field</div>
                      </template>
                    </app-form-group>
                  </template>
                  <template #item-3>
                    <app-form-group label="2$ Bills" label-for="2_bills" required>
                      <app-input
                        v-model.number="form_balance.usd_denomination.two"
                        :error="$v.form_balance.usd_denomination.two.$error"
                        id="2_bills"
                        name="2_bills"
                        type="number"
                      />
                      <template #error>
                        <div v-if="$v.form_balance.usd_denomination.two.$dirty && !$v.form_balance.usd_denomination.two.required">Required field</div>
                      </template>
                    </app-form-group>
                  </template>
                </app-grid>
                <app-grid col="3" items="3">
                  <template #item-1>
                    <app-form-group label="1$ Bills" label-for="1_bills" required>
                      <app-input
                        v-model.number="form_balance.usd_denomination.one"
                        :error="$v.form_balance.usd_denomination.one.$error"
                        id="1_bills"
                        name="1_bills"
                        type="number"
                      />
                      <template #error>
                        <div v-if="$v.form_balance.usd_denomination.one.$dirty && !$v.form_balance.usd_denomination.one.required">Required field</div>
                      </template>
                    </app-form-group>
                  </template>
                </app-grid>
              </template>
            </app-grid>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_balance" :disabled="$v.form_balance.$error">Add</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="exchange-modal"
      :width="450"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('exchange-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ exchange_to_edit ? 'Edit exchange' : 'Exchange' }}</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormExchange">
            <app-form-group label="Date" required>
              <app-date-picker
                v-model="exchange_form.date"
                placeholder="Specify a date"
                :error="$v.exchange_form.date.$error"
              />
              <template #error>
                <div v-if="$v.exchange_form.date.$dirty && !$v.exchange_form.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-grid>
              <template #item-1>
                <app-form-group label="Currency you give" required>
                  <app-select
                    v-model="exchange_form.from_account"
                    :options="account_options_transfer"
                    :clearable="false"
                    :display-field="option => option.currency.code"
                    placeholder="Currency"
                    :error="$v.exchange_form.from_account.$error"
                  />
                  <template #error>
                    <div v-if="$v.exchange_form.from_account.$dirty && !$v.exchange_form.from_account.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group
                  label="Amount you give"
                  label-for="transfer_amount"
                  required
                >
                  <app-fin-number
                    v-model="exchange_form.from_amount"
                    :error="$v.exchange_form.from_amount.$error"
                    id="transfer_amount"
                    placeholder="0"
                    name="transfer_amount"
                  />
                  <template #error>
                    <div v-if="$v.exchange_form.from_amount.$dirty && !$v.exchange_form.from_amount.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <app-grid>
              <template #item-1>
                <app-form-group label="Currency you receive" required>
                  <app-select
                    v-model="exchange_form.to_account"
                    :options="account_options_transfer"
                    :clearable="false"
                    :display-field="option => option.currency.code"
                    placeholder="Currency"
                    :error="$v.exchange_form.to_account.$error"
                  />
                  <template #error>
                    <div v-if="$v.exchange_form.to_account.$dirty && !$v.exchange_form.to_account.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group
                  label="Amount you receive"
                  label-for="received_amount"
                  required
                >
                  <app-fin-number
                    v-model="exchange_form.to_amount"
                    :error="$v.exchange_form.to_amount.$error"
                    id="received_amount"
                    placeholder="0"
                    name="received_amount"
                  />
                  <template #error>
                    <div v-if="$v.exchange_form.to_amount.$dirty && !$v.exchange_form.to_amount.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <app-form-group label="Description" label-for="description">
              <app-input
                v-model="exchange_form.description"
                id="description"
                placeholder="Description"
                name="description"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_exchange" :disabled="$v.exchange_form.$error">
                {{ !exchange_to_edit ? 'Exchange' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete transaction
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this transaction?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteTransaction" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteFinanceCashflow,
  getEmployeesListSearch,
  getFinanceAccounts,
  getFinanceTransactionsItemsList,
  getHotelManagementCashflow,
  patchFinanceCashflow,
  postFinanceCashflowReceptionOutcome,
  postFinanceCashflowTransfer,
  postHotelManagementBalanceHistory,
  postHotelManagementCashflowTransferToOffice,
} from '@/http'
import { format, parse } from 'date-fns'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'HotelManagementBalance',
  data() {
    return {
      is_loaded: false,
      table_data: [],
      form: {
        items: [{}],
      },
      form_balance: {
        usd_denomination: {},
      },
      exchange_form: {
        date: format(new Date(), 'yyyy-MM-dd')
      },
      form_transfer: {},
      employee_options: [],
      transaction_items_options: [],
      account_options: [],
      exchange_to_edit: null,
      transaction_to_action: null,
      account_options_transfer: [],
    }
  },
  validations: {
    form: {
      account: { required },
      description: { required },
      items: {
        $each: {
          amount: { required },
          item: { required },
        }
      }
    },
    form_transfer: {
      from_account: { required },
      from_amount: { required },
    },
    exchange_form: {
      from_account: { required },
      to_account: { required },
      from_amount: { required },
      date: { required },
      to_amount: { required },
    },
    form_balance: {
      usd_amount: { required },
      tzs_amount: { required },
      eur_amount: { required },
      usd_denomination: {
        hundred: { required },
        fifty: { required },
        twenty: { required },
        ten: { required },
        five: { required },
        two: { required },
        one: { required },
      }
    }
  },
  created() {
    this.fetchItems()
    getFinanceAccounts({ property: this.$store.state.userData.hotel_property?.code })
      .then(response => {
        this.account_options = response.data
      })
    getFinanceTransactionsItemsList({ expense: true, property: this.$store.state.userData.hotel_property?.code })
      .then(response => { this.transaction_items_options = response.data })
    getFinanceAccounts({ transfer: true }).then(response => { this.account_options_transfer = response.data })
    getEmployeesListSearch({ status: 'working_resident' }).then(res => { this.employee_options = res.data })
  },
  methods: {
    fetchItems() {
      getHotelManagementCashflow()
        .then(response => {
          this.table_data = response.data
          this.is_loaded = true
        })
    },
    onCheckFormBalance() {
      this.$v.form_balance.$touch()
      if (this.$v.form_balance.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormBalance()
      }
    },
    sendFormBalance() {
      this.$refs.submit_balance.preload = true
      const data = {
        ...this.form_balance,
        usd_denomination: {
          100: this.form_balance.usd_denomination.hundred,
          50: this.form_balance.usd_denomination.fifty,
          20: this.form_balance.usd_denomination.twenty,
          10: this.form_balance.usd_denomination.ten,
          5: this.form_balance.usd_denomination.five,
          2: this.form_balance.usd_denomination.two,
          1: this.form_balance.usd_denomination.one,
        },
      }
      postHotelManagementBalanceHistory(data)
        .then(() => {
          this.fetchItems()
          this.$modal.hide('balance-modal')
        })
        .catch(() => { this.$refs.submit_balance.preload = false })
    },
    onCheckFormTransfer() {
      this.$v.form_transfer.$touch()
      if (this.$v.form_transfer.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormTransfer()
      }
    },
    sendFormTransfer() {
      this.$refs.submit_transfer.preload = true
      postHotelManagementCashflowTransferToOffice(this.form_transfer)
        .then(() => {
          this.fetchItems()
          this.$refs.submit_transfer.preload = false
          this.$modal.hide('transfer-modal')
        })
        .catch(() => { this.$refs.submit_transfer.preload = false })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (!this.transaction_to_action) {
        postFinanceCashflowReceptionOutcome(this.normalizeForm())
          .then(() => {
            this.fetchItems()
            this.$refs.submit.preload = false
            this.$modal.hide('transaction-modal')
          })
          .catch(() => { this.$refs.submit.preload = false })
      } else {
        patchFinanceCashflow(this.transaction_to_action.id, this.normalizeForm())
          .then(() => {
            this.fetchItems()
            this.$refs.submit.preload = false
            this.$modal.hide('transaction-modal')
          })
          .catch(() => { this.$refs.submit.preload = false })
      }
    },
    onCheckFormExchange() {
      this.$v.exchange_form.$touch()
      if (this.$v.exchange_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormExchange()
      }
    },
    sendFormExchange() {
      this.$refs.submit_exchange.preload = true
      postFinanceCashflowTransfer(this.normalizeFormExchange())
        .then(() => {
          this.fetchItems()
          this.$refs.submit_exchange.preload = false
          this.$modal.hide('exchange-modal')
        })
        .catch(() => { this.$refs.submit_exchange.preload = false })

      // if (!this.transaction_to_edit) {
      //
      // } else {
      //   putFinanceCashflowTransfer(this.transaction_to_edit.id, this.normalizeEditFormTransfer())
      //     .then(() => {
      //       this.fetchItems()
      //       this.$refs.submit_exchange.preload = false
      //       this.$modal.hide('transfer-modal')
      //       this.transaction_to_edit = null
      //     })
      //     .catch(() => { this.$refs.submit_exchange.preload = false })
      // }
    },
    normalizeForm() {
      const form = {...this.form}
      form.type = 2
      form.account = form.account.id
      if (form.responsible) form.responsible = form.responsible.id
      form.items = form.items.map(({amount, item}) => ({
        amount,
        item: item.id,
      }))
      return form
    },
    normalizeFormExchange() {
      const form = {}
      for (const property in this.exchange_form) {
        if (this.exchange_form[property]) form[property] = this.exchange_form[property]
      }
      if (form.from_account?.currency === form.to_account?.currency) delete form.to_amount
      form.from_account = form.from_account.id
      form.to_account = form.to_account.id

      return form
    },
    onShowModal(transaction) {
      this.transaction_to_action = transaction
      if (!transaction) {
        this.form = {
          items: [{}],
        }
      } else {
        this.form = {
          account: transaction.account,
          description: transaction.description,
          items: transaction.items,
        }
      }
      this.$v.form.$reset()
      this.$modal.show('transaction-modal')
    },
    onShowModalTransfer() {
      this.form_transfer = {}
      this.$v.form_transfer.$reset()
      this.$modal.show('transfer-modal')
    },
    onShowModalBalance() {
      this.form_balance = {
        usd_denomination: {}
      }
      this.$v.form_balance.$reset()
      this.$modal.show('balance-modal')
    },
    onShowModalExchange() {
      this.exchange_form = {}
      this.exchange_form = {
        date: format(new Date(), 'yyyy-MM-dd'),
      }
      this.$v.exchange_form.$reset()
      this.$modal.show('exchange-modal')
    },
    onShowDeleteModal(transaction) {
      this.transaction_to_action = transaction
      this.$modal.show('delete-modal')
    },
    onDeleteTransaction() {
      this.$refs.delete.preload = true
      deleteFinanceCashflow(this.transaction_to_action.id)
        .then(() => {
          this.fetchItems()
          this.$refs.delete.preload = false
          this.$modal.hide('delete-modal')
        })
        .catch(() => {
          this.$refs.delete.preload = false
        })
    },
    onAddItem() {
      this.form.items.push({})
    },
    onRemoveItem(index) {
      this.form.items.splice(index, 1)
    },
    tooltipContentCashflowItems(transaction) {
      let content = ''
      transaction.items.forEach(({item, amount}) => {
        content += `<span><b>Item:</b> ${transaction.type === 1 ? item.name_revenue : item.name_expense} <b>Amount:</b> ${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(amount)} ${transaction.account.currency}</span>`
      })
      return content
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
  }
}
</script>

<style scoped lang="sass">
.transaction__table
  background-color: #fff

.transaction__table-row
  display: grid
  align-items: center
  grid-template-columns: 120px 180px 180px 1fr 185px 80px
  border: 1px solid #eaeaea
  border-top: none

  &:first-child
    border-color: #f6f6f6
    background-color: #f6f6f6
    border-radius: 5px 5px 0 0

.transaction__table-row--head .transaction__table-col
  font-weight: 500

.transaction__table-col
  padding: 10px 12px

  b
    font-weight: 500

.transaction__table-btn
  font-weight: 500
  text-align: left
  transition: color 0.3s

  &:hover
    color: $color-theme

  span
    color: #626262
    font-size: 13px

.transaction__table-col--flex-col
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 3px

.transaction__table-col--flex
  display: flex
  gap: 10px
  align-items: center
  justify-content: center

  button
    line-height: 0
    transition: transform 0.2s

    &:hover
      transform: translateY(-1px)

    img
      width: 25px
      height: 25px

.transaction__table-amount--green
  color: $color-theme

.transaction__table-amount--red
  color: #e85c20

.transaction__table-description
  color: #626262
  font-size: 13px

.transaction__table-balance
  position: relative
  padding-left: 20px

  &:before
    content: ''
    position: absolute
    left: 8px
    top: 5px
    width: 8px
    height: 5px

.transaction__table-balance--minus:before
    background-image: url("~@/assets/img/balance-minus.svg")

.transaction__table-balance--plus:before
  background-image: url("~@/assets/img/balance-plus.svg")
</style>
